import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, Heart, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { products } from '../data/products';

const NavLinks = ({ onClick }) => (
  <>
    <Link to="/" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">Home</Link>
    <Link to="/collections/all" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">Shop</Link>
    <Link to="/quiz" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24] inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Quiz</Link>
    <Link to="/glossary" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">Glossary</Link>
    <Link to="/recipes" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">Recipes</Link>
    <Link to="/archive" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">Archive</Link>
    <Link to="/about" onClick={onClick} className="link-underline text-sm uppercase tracking-widest text-[#2f2a24]">About</Link>
  </>
);

const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const searchResults = query.trim()
    ? products.filter(p => p.title.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-[#3a4a3b] text-[#f6f2ea] overflow-hidden">
        <div className="marquee-track py-2 text-xs uppercase tracking-[0.25em]">
          {['ann-30day', 'ann-heart', 'ann-natural', 'ann-medicine'].map((k, j) => {
            const messages = { 'ann-30day': '30-day money-back guarantee', 'ann-heart': 'Herbs. Healing. Heart.', 'ann-natural': 'All-natural, handcrafted with love', 'ann-medicine': 'Where food becomes medicine' };
            return (
              <div key={k} className="flex items-center">
                <span className="px-8">&#10022; {messages[k]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main header */}
      <header className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? 'bg-[#faf7f1]/95 backdrop-blur border-b border-[#e5decf]' : 'bg-[#faf7f1] border-b border-[#e5decf]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            {/* Left */}
            <div className="flex items-center gap-6">
              <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </button>
              <nav className="hidden lg:flex items-center gap-8">
                <NavLinks />
              </nav>
            </div>

            {/* Center logo */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 text-center">
              <div className="font-serif-elegant text-2xl lg:text-3xl text-[#2f2a24] leading-none">Herbs We Love</div>
              <div className="font-script text-sm text-[#7a8b6b] mt-1">herbs &middot; healing &middot; heart</div>
            </Link>

            {/* Right */}
            <div className="flex items-center gap-5">
              <button onClick={() => setSearchOpen(true)} aria-label="Search" className="hover:opacity-70 transition-opacity">
                <Search className="w-5 h-5" />
              </button>
              <Link to="/wishlist" aria-label="Wishlist" className="relative hover:opacity-70 transition-opacity">
                <Heart className="w-5 h-5" />
                {wishCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#7a3b2e] text-[#faf7f1] text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-medium">{wishCount}</span>
                )}
              </Link>
              <Link to="/about" aria-label="Account" className="hidden sm:block hover:opacity-70 transition-opacity">
                <User className="w-5 h-5" />
              </Link>
              <button onClick={() => setDrawerOpen(true)} aria-label="Cart" className="relative hover:opacity-70 transition-opacity">
                <ShoppingBag className="w-5 h-5" />
                {count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#3a4a3b] text-[#faf7f1] text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-medium">{count}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => setSearchOpen(false)}>
          <div className="bg-[#faf7f1] border-b border-[#e5decf] p-6" onClick={e => e.stopPropagation()}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 border-b border-[#3a4a3b] pb-3">
                <Search className="w-5 h-5" />
                <input
                  autoFocus
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search products..."
                  className="flex-1 bg-transparent outline-none text-lg"
                />
                <button onClick={() => setSearchOpen(false)}><X className="w-5 h-5" /></button>
              </div>
              {searchResults.length > 0 && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {searchResults.map(p => (
                    <button
                      key={p.id}
                      onClick={() => { navigate(`/products/${p.id}`); setSearchOpen(false); setQuery(''); }}
                      className="flex items-center gap-3 p-2 hover:bg-[#efe9db] transition-colors text-left"
                    >
                      <img src={p.images[0]} alt={p.title} className="w-14 h-14 object-cover" />
                      <div>
                        <div className="text-sm font-medium">{p.title}</div>
                        <div className="text-xs text-[#7a8b6b]">${p.price.toFixed(2)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {query && searchResults.length === 0 && (
                <div className="mt-6 text-sm text-[#7a8b6b]">No products found for "{query}".</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#faf7f1] p-6 flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <div className="font-serif-elegant text-xl">Herbs We Love</div>
              <button onClick={() => setMobileOpen(false)}><X className="w-6 h-6" /></button>
            </div>
            <nav className="flex flex-col gap-5">
              <NavLinks onClick={() => setMobileOpen(false)} />
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
